import type { JSX } from "react";
import { CFG } from "../../data/haki.ts";
import { bomState } from "../../state/bom/bom.state.ts";

/** Extension above which the gauge turns amber, millimetres. */
const HOT_EXTENSION = 450;

type StatProps = Readonly<{ value: string | number; label: string }>;

function Stat({ value, label }: StatProps): JSX.Element {
  return (
    <div className="flex-1 rounded border border-slate-600/50 bg-slate-900/40 px-2 py-1.5 text-center">
      <b className="block font-mono text-base">{value}</b>
      <span className="text-[10px] uppercase tracking-wider text-slate-400">{label}</span>
    </div>
  );
}

/**
 * Base-jack solution summary.
 *
 * Every leg's jack must land within the BS 55–570 mm range; the 500 mm node
 * pitch sits inside it, so a solution always exists — this panel shows how
 * much of the range the tallest one is using.
 */
export function BaseJackPanel(): JSX.Element {
  const { jackExtensions, raisedCount } = bomState.use();
  const count = jackExtensions.length;
  const max = count > 0 ? Math.max(...jackExtensions) : 0;
  const min = count > 0 ? Math.min(...jackExtensions) : 0;
  const fill = count > 0 ? ((max - CFG.jackMin) / (CFG.jackMax - CFG.jackMin)) * 100 : 0;

  return (
    <div className="flex flex-col gap-3 p-3">
      <div>
        <div className="mb-1 flex justify-between text-xs text-slate-400">
          <span>Max extension</span>
          <b className="font-mono text-slate-100">{count > 0 ? `${max} mm` : "— mm"}</b>
        </div>
        <div className="h-2.5 overflow-hidden rounded-full border border-slate-600/50 bg-slate-900/60">
          <div
            className={`h-full rounded-full transition-[width] ${max > HOT_EXTENSION ? "bg-amber-500" : "bg-emerald-500"}`}
            style={{ width: `${Math.min(100, Math.max(0, fill))}%` }}
          />
        </div>
      </div>

      <div className="flex gap-2">
        <Stat value={count} label="standards" />
        <Stat value={raisedCount} label="raised base" />
        <Stat value={count > 0 ? min : "—"} label="min mm" />
      </div>
    </div>
  );
}
